"use client";

import { Component, createRef, type ReactNode } from "react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { filterByWordPrefixes, hasWordPrefixQuery } from "~/lib/search/word-prefix-search";
import type { GameAnalysisListResponse } from "./contracts";
import type { ListFilters, OpeningLookup } from "./listProjection";

type Selection = NonNullable<ListFilters["openings"]>;
type Catalog = GameAnalysisListResponse["openingGroups"] | undefined;
type FocusSnapshot = { element: HTMLButtonElement; openingId?: string } | null;
type CatalogFocusProps = { catalog: Catalog; children: ReactNode };

// Capture actual focus before React removes catalog nodes. A remembered last-focus
// ref would confuse an intentional blur with focus lost during the catalog commit.
class OpeningCatalogFocus extends Component<CatalogFocusProps, Record<string, never>, FocusSnapshot> {
  private readonly editor = createRef<HTMLDivElement>();

  getSnapshotBeforeUpdate(previous: Readonly<CatalogFocusProps>): FocusSnapshot {
    const active = document.activeElement;
    if (previous.catalog === this.props.catalog || !(active instanceof HTMLButtonElement) || !this.editor.current?.contains(active)) return null;
    const openingId = active.dataset.openingId;
    if (openingId !== undefined) return { element: active, openingId };
    return active.dataset.openingGroupId === undefined && active.dataset.unavailableOpeningId === undefined
      ? null : { element: active };
  }

  componentDidUpdate(_previous: Readonly<CatalogFocusProps>, _state: Readonly<Record<string, never>>, snapshot: FocusSnapshot) {
    if (!snapshot || (snapshot.element.isConnected && !snapshot.element.disabled)) return;
    // A different real focus destination always takes precedence over recovery.
    if (document.activeElement !== document.body && document.activeElement !== snapshot.element) return;
    const replacement = snapshot.openingId === undefined ? null
      : this.editor.current?.querySelector<HTMLButtonElement>(`[data-opening-id="${snapshot.openingId}"]:not(:disabled)`);
    (replacement ?? this.editor.current?.querySelector<HTMLInputElement>('input[type="search"]'))?.focus();
  }

  render() { return <div ref={this.editor} className="space-y-3">{this.props.children}</div>; }
}

export function OpeningFilterEditor({ groups, openings, selection = { ids: [] }, onChange, query, onQueryChange }: {
  groups: GameAnalysisListResponse["openingGroups"] | undefined; openings: OpeningLookup; selection?: Selection; onChange: (selection: Selection) => void; query: string; onQueryChange: (query: string) => void;
}) {
  const searching = hasWordPrefixQuery(query);
  const visibleGroups = groups?.map((group) => ({
    ...group,
    openings: filterByWordPrefixes(group.openings, query, (opening) => opening.title),
  })).filter((group) => !searching || group.openings.length > 0);
  const changeIds = (ids: readonly number[], checked: boolean) => {
    const next = new Set(selection.ids);
    for (const id of ids) {
      if (checked) next.add(id); else next.delete(id);
    }
    onChange({ ...selection, ids: [...next] });
  };
  return <OpeningCatalogFocus catalog={groups}>
      <Input type="search" aria-label="Поиск по названию дебюта" placeholder="Фильтр по названию"
        value={query} onChange={(event) => onQueryChange(event.target.value)} />
      <Button type="button" variant="outline" onClick={() => onQueryChange("")}>Очистить поиск</Button>
      <div className="space-y-3">
        <label className="flex items-center gap-2"><Checkbox checked={selection.withoutOpening === true} onCheckedChange={(checked) => onChange({ ...selection, withoutOpening: checked === true })} />Без дебюта</label>
        <label className="flex items-center gap-2"><Checkbox checked={selection.unavailable === true} onCheckedChange={(checked) => onChange({ ...selection, unavailable: checked === true })} />Недоступные дебюты</label>
        {groups === undefined && <p className="text-sm text-muted-foreground">Каталог дебютов не загружен</p>}
        {groups !== undefined && selection.ids.filter((id) => !openings.has(id)).map((id) => <label key={id} className="flex items-center gap-2"><Checkbox data-unavailable-opening-id={id} checked onCheckedChange={() => changeIds([id], false)} />Недоступный дебют</label>)}
        {groups !== undefined && searching && visibleGroups?.length === 0 && <p className="text-sm text-muted-foreground">Дебюты не найдены</p>}
        {visibleGroups?.map((group) => {
          const ids = group.openings.map(({ problemId }) => problemId);
          const count = ids.filter((id) => selection.ids.includes(id)).length;
          return <div key={group.problemSetId}>
            <label className="flex items-center gap-2 font-medium"><Checkbox data-opening-group-id={group.problemSetId} disabled={ids.length === 0} checked={count === 0 ? false : count === ids.length ? true : "indeterminate"} onCheckedChange={(checked) => changeIds(ids, checked === true)} />{group.name}</label>
            <div className="ml-6 space-y-2 pt-2">{group.openings.map((opening) => <label key={opening.problemId} className="flex items-center gap-2"><Checkbox data-opening-id={opening.problemId} checked={selection.ids.includes(opening.problemId)} onCheckedChange={(checked) => changeIds([opening.problemId], checked === true)} />{opening.title}</label>)}</div>
          </div>;
        })}
      </div>
    </OpeningCatalogFocus>;
}
